import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminApi, organizationApi, Admin, Organization } from '../../services/api';
import { CodeDisplayModal } from '../../components/CodeDisplayModal';

export function AdminsList(): JSX.Element {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formOrgId, setFormOrgId] = useState<number | ''>('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [adminsRes, orgsRes] = await Promise.all([
        adminApi.getAll(),
        organizationApi.getAll(),
      ]);
      setAdmins(adminsRes.admins || []);
      setOrganizations(orgsRes.organizations || []);
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    try {
      const response = await adminApi.create({
        name: formName,
        organizationId: formOrgId || undefined,
      });

      setGeneratedCode(response.generatedCode);
      setShowCreateForm(false);
      setFormName('');
      setFormOrgId('');
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'Fehler beim Erstellen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegenerate = async (admin: Admin) => {
    if (!window.confirm(`Neuen Code für "${admin.name}" generieren? Der alte Code wird ungültig.`)) return;

    try {
      const response = await adminApi.regenerateCode(admin.id);
      setGeneratedCode(response.generatedCode);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Regenerieren');
    }
  };

  const handleToggleActive = async (admin: Admin) => {
    if (admin.isSuperAdmin) {
      setError('Super-Admin kann nicht deaktiviert werden');
      return;
    }

    try {
      await adminApi.update(admin.id, { isActive: !admin.isActive });
      loadData();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Aktualisieren');
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Laden...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Administratoren</h1>
        {user?.isSuperAdmin && !showCreateForm && (
          <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
            Neuer Administrator
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {generatedCode && (
        <CodeDisplayModal
          code={generatedCode}
          onClose={() => setGeneratedCode(null)}
          title="Admin-Code generiert"
        />
      )}

      {showCreateForm && (
        <div className="form-card">
          <h2>Neuer Administrator</h2>
          <form onSubmit={handleCreate}>
            {formError && <div className="form-error">{formError}</div>}

            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="z.B. Max Mustermann"
                required
                minLength={2}
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label htmlFor="orgId">Organisation (optional)</label>
              <select
                id="orgId"
                value={formOrgId}
                onChange={e => setFormOrgId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">-- Keine Zuordnung --</option>
                {organizations.filter(o => o.isActive).map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({org.storeId})
                  </option>
                ))}
              </select>
              <small>Wenn zugeordnet, nutzt der Admin die Einstellungen der Organisation</small>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Erstellen...' : 'Erstellen'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreateForm(false)}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Rolle</th>
              <th>Organisation</th>
              <th>Status</th>
              <th>Erstellt</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 ? (
              <tr>
                <td colSpan={6} className="no-data">Keine Administratoren vorhanden</td>
              </tr>
            ) : (
              admins.map(admin => (
                <tr key={admin.id} className={!admin.isActive ? 'inactive' : ''}>
                  <td>{admin.name}</td>
                  <td>
                    {admin.isSuperAdmin ? (
                      <span className="role-badge super">Super-Admin</span>
                    ) : (
                      <span className="role-badge admin">Admin</span>
                    )}
                  </td>
                  <td>{admin.organization?.name || '-'}</td>
                  <td>
                    <span className={`status-badge ${admin.isActive ? 'active' : 'inactive'}`}>
                      {admin.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td>{new Date(admin.createdAt).toLocaleDateString('de-DE')}</td>
                  <td className="actions">
                    {user?.isSuperAdmin && (
                      <>
                        <button className="btn btn-small" onClick={() => handleRegenerate(admin)}>
                          Code neu
                        </button>
                        {!admin.isSuperAdmin && (
                          <button
                            className={`btn btn-small ${admin.isActive ? 'btn-danger' : 'btn-success'}`}
                            onClick={() => handleToggleActive(admin)}
                          >
                            {admin.isActive ? 'Deaktivieren' : 'Aktivieren'}
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminsList;
